'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useConfig } from '@/core/config/manager';
import { paymentService } from '@/services';
import { shippingService } from '@/services';
import { isDebug } from '@/services/env.service';

/**
 * Development Status Bar
 * Shows basic configuration info at the bottom of the page in development mode
 */

export function DevStatusBar() {
  const config = useConfig();
  const pathname = usePathname();
  const [isClient, setIsClient] = useState(false);
  const startTimeRef = useRef<number>(typeof performance !== 'undefined' ? performance.now() : 0);
  const [pageLoadMs, setPageLoadMs] = useState<number>(0);
  const [popupData, setPopupData] = useState<null | { title: string; data: unknown; durationMs: number }>(null);

  useEffect(() => {
    setIsClient(true);

    // Track “time since navigation started” (performance.now) and stop once the page is fully loaded.
    const capMs = 15_000;
    let intervalId: number | null = null;

    const updateTimer = () => {
      const elapsed = Math.round(performance.now() - startTimeRef.current);
      setPageLoadMs(elapsed);
      return elapsed;
    };

    const stopTimer = () => {
      updateTimer();
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };

    intervalId = window.setInterval(() => {
      const elapsed = updateTimer();
      if (elapsed >= capMs) {
        stopTimer();
      }
    }, 250);

    if (document.readyState === 'complete') {
      stopTimer();
    } else {
      window.addEventListener('load', stopTimer, { once: true });
    }

    return () => {
      stopTimer();
      window.removeEventListener('load', stopTimer);
    };
  }, []);

  // Only show in development/staging and on client side
  // `isDebug()` can also force showing in production builds (via NEXT_PUBLIC_DEBUG=1)
  const isDevEnvironment = config?.app.environment === 'development' || config?.app.environment === 'staging';
  const showBar = Boolean(config && (isDevEnvironment || isDebug()) && isClient);
  if (!showBar) {
    return null;
  }

  const safeConfig = config!;
  const activeTheme = safeConfig.theme.activeTheme;
  const defaultLanguage = safeConfig.i18n.defaultLanguage;
  const availableLanguages = safeConfig.i18n.availableLanguages.join(', ');

  const showPopup = (title: string, data: unknown, durationMs: number) => {
    setPopupData({ title, data, durationMs });
  };

  const closePopup = () => setPopupData(null);

  const loadShippingConfig = async () => {
    const start = performance.now();
    const data = await shippingService.getShippingProviders();
    const durationMs = Math.round(performance.now() - start);
    showPopup('Shipping Providers', data, durationMs);
  };

  const loadPaymethodConfig = async () => {
    const start = performance.now();
    const data = await paymentService.getPaymentMethods();
    const durationMs = Math.round(performance.now() - start);
    showPopup('Payment Methods', data, durationMs);
  };

  const loadConfig = async () => {
    const start = performance.now();
    // config is already loaded via useConfig, but we can snapshot it
    const durationMs = Math.round(performance.now() - start);
    showPopup('Full Config', config, durationMs);
  };

  return (
    <>
      {popupData && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1.5rem',
          }}
          onClick={closePopup}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#111827',
              color: '#f9fafb',
              borderRadius: '0.75rem',
              padding: '1rem',
              maxWidth: '90vw',
              maxHeight: '90vh',
              overflow: 'auto',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem' }}>{popupData.title}</h2>
              <button
                onClick={closePopup}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#f9fafb',
                  cursor: 'pointer',
                  fontSize: '1.2rem',
                }}
              >
                ×
              </button>
            </div>
            <div style={{ marginBottom: '0.75rem', fontSize: '0.75rem', color: '#9ca3af' }}>Loaded in {popupData.durationMs}ms</div>
            <pre style={{ fontSize: '0.65rem', lineHeight: 1.3, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{JSON.stringify(popupData.data, null, 2)}</pre>
          </div>
        </div>
      )}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#1f2937',
          color: '#f9fafb',
          padding: '0.5rem 1rem',
          fontSize: '0.75rem',
          fontFamily: 'monospace',
          borderTop: '1px solid #374151',
          zIndex: 9999,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <span>
            Theme: <strong>{activeTheme}</strong>
          </span>
          <div style={{ display: 'flex', flex: 1, justifyContent: 'center', gap: '1.5rem', alignItems: 'center' }}>
            <span>
              Path: <strong>{pathname}</strong>
            </span>
            <span>
              Lang: <strong>{defaultLanguage}</strong> ({availableLanguages})
            </span>
          </div>
          <button
            onClick={loadConfig}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.3rem',
              color: '#f9fafb',
              cursor: 'pointer',
            }}
          >
            Config
          </button>
          <button
            onClick={loadShippingConfig}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.3rem',
              color: '#f9fafb',
              cursor: 'pointer',
            }}
          >
            Shipping
          </button>
          <button
            onClick={loadPaymethodConfig}
            style={{
              background: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.15)',
              padding: '0.2rem 0.5rem',
              borderRadius: '0.3rem',
              color: '#f9fafb',
              cursor: 'pointer',
            }}
          >
            Paymethods
          </button>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span>
            Env: <strong>{safeConfig.app.environment}</strong>
          </span>
          <span>
            Page load:{' '}
            <strong>
              {(pageLoadMs / 1000).toFixed(2)}s / {pageLoadMs}ms
            </strong>
          </span>
        </div>
      </div>
    </>
  );
}
